import { Head, Link, router } from '@inertiajs/react'
import { type ReactNode, useState } from 'react'
import { Button } from '@/components/ui/button'
import { spotifyEmbedUrl, youtubeEmbedUrl } from '@/lib/embeds'

type RecordingKind = 'file' | 'spotify' | 'youtube'
type Accent = 'learning' | 'refresh' | 'play'

interface PrimaryRecording {
  id: number
  kind: RecordingKind
  url: string | null
  audio_url: string | null
}

interface LauncherTune {
  id: number
  name: string
  tune_type: string | null
  musical_key: string | null
  primary_recording: PrimaryRecording | null
}

interface PlayTune {
  id: number
  name: string
  primary_recording: PrimaryRecording | null
}

interface LauncherSet {
  id: number
  name: string
  tune_names: string[]
  primary_recording: PrimaryRecording | null
}

interface Props {
  learning: LauncherTune[]
  refresh: LauncherTune[]
  play_pool: PlayTune[]
  learning_count: number
  sets: LauncherSet[]
}

const accentText: Record<Accent, string> = {
  learning: 'text-learning',
  refresh: 'text-refresh',
  play: 'text-play',
}

const accentSpine: Record<Accent, string> = {
  learning: 'bg-learning',
  refresh: 'bg-refresh',
  play: 'bg-play',
}

const MODE = /(major|minor|dorian|mixolydian|aeolian|ionian|lydian|phrygian|locrian)$/i

function formatKey(key: string) {
  return key.replace(MODE, (m) => ` ${m[0].toUpperCase()}${m.slice(1).toLowerCase()}`).trim()
}

function meta(tune: { tune_type: string | null; musical_key: string | null }) {
  return [tune.tune_type, tune.musical_key && formatKey(tune.musical_key)]
    .filter(Boolean)
    .join('  ·  ')
}

function patch(path: string) {
  router.patch(path, {}, { preserveScroll: true })
}

function EmbedPlayer({
  label,
  colorClass,
  onPlay,
  children,
}: {
  label: string
  colorClass: string
  onPlay?: () => void
  children: ReactNode
}) {
  const [mounted, setMounted] = useState(false)

  if (!mounted) {
    return (
      <button
        type="button"
        onClick={() => {
          setMounted(true)
          onPlay?.()
        }}
        className={`inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 hover:underline ${colorClass}`}
      >
        <span aria-hidden>♪</span> Play on {label}
      </button>
    )
  }

  return <>{children}</>
}

function SpotifyEmbed({ src, onPlay }: { src: string; onPlay?: () => void }) {
  return (
    <EmbedPlayer label="Spotify" colorClass="text-[#1db954]" onPlay={onPlay}>
      <iframe
        title="Spotify player"
        src={src}
        loading="lazy"
        allow="encrypted-media"
        className="w-full rounded-xl border-0"
        height={152}
      />
    </EmbedPlayer>
  )
}

function YouTubeEmbed({ src, onPlay }: { src: string; onPlay?: () => void }) {
  return (
    <EmbedPlayer label="YouTube" colorClass="text-[#ff0000]" onPlay={onPlay}>
      <iframe
        title="YouTube player"
        src={src}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="aspect-video w-full rounded-xl border-0"
      />
    </EmbedPlayer>
  )
}

function PlayLink({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/70 underline-offset-4 hover:text-foreground hover:underline"
    >
      <span aria-hidden>♪</span> Play on {label}
    </a>
  )
}

function RecordingPlayer({
  recording,
  onPlay,
}: {
  recording: PrimaryRecording | null
  onPlay?: () => void
}) {
  if (recording?.kind === 'file' && recording.audio_url) {
    return (
      <audio controls src={recording.audio_url} className="h-9 w-full" onPlay={onPlay}>
        <track kind="captions" />
      </audio>
    )
  }

  if (recording?.url) {
    if (recording.kind === 'spotify') {
      const embed = spotifyEmbedUrl(recording.url)
      if (embed) return <SpotifyEmbed src={embed} onPlay={onPlay} />
    }
    if (recording.kind === 'youtube') {
      const embed = youtubeEmbedUrl(recording.url)
      if (embed) return <YouTubeEmbed src={embed} onPlay={onPlay} />
    }
    return (
      <PlayLink url={recording.url} label={recording.kind === 'youtube' ? 'YouTube' : 'Spotify'} />
    )
  }

  return <span className="text-sm italic text-muted-foreground">no recording yet</span>
}

function SectionHeading({
  kicker,
  title,
  accent,
  aside,
}: {
  kicker: string
  title: string
  accent: Accent
  aside?: ReactNode
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        <p
          className={`text-[0.7rem] font-semibold uppercase tracking-[0.22em] ${accentText[accent]}`}
        >
          {kicker}
        </p>
        <h2 className="font-display text-3xl font-medium tracking-tight text-foreground">
          {title}
        </h2>
      </div>
      {aside}
    </div>
  )
}

function TuneCard({
  href,
  name,
  metaLine,
  accent,
  delay,
  children,
}: {
  href: string
  name: string
  metaLine: string
  accent: Accent
  delay: number
  children: ReactNode
}) {
  return (
    <article
      className="animate-rise relative overflow-hidden rounded-lg border border-border bg-card shadow-[0_1px_2px_rgba(60,40,20,0.06),0_8px_24px_-12px_rgba(60,40,20,0.18)]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${accentSpine[accent]}`} aria-hidden />
      <div className="space-y-4 px-5 py-4 pl-6">
        <div>
          <Link
            href={href}
            className="font-display text-2xl font-medium leading-tight tracking-tight text-foreground decoration-border underline-offset-[6px] hover:underline"
          >
            {name}
          </Link>
          {metaLine && (
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {metaLine}
            </p>
          )}
        </div>
        {children}
      </div>
    </article>
  )
}

export default function Launcher({ learning, refresh, play_pool, learning_count, sets }: Props) {
  const [shuffled, setShuffled] = useState<PlayTune | null>(null)
  const [nowPlaying, setNowPlaying] = useState<string | null>(null)

  function shuffle() {
    if (play_pool.length === 0) return
    if (play_pool.length === 1) return setShuffled(play_pool[0])
    let pick = shuffled
    while (!pick || pick.id === shuffled?.id) {
      pick = play_pool[Math.floor(Math.random() * play_pool.length)]
    }
    setShuffled(pick)
  }

  return (
    <>
      <Head title={nowPlaying ? `♪ ${nowPlaying}` : "What'll it be?"} />

      <div className="space-y-12 pb-16">
        <header className="animate-rise space-y-1">
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Pick up the mandolin
          </p>
          <h1 className="font-display text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
            What'll it be?
          </h1>
        </header>

        <section>
          <SectionHeading
            kicker="Homework"
            title="Learning"
            accent="learning"
            aside={
              learning_count > 5 ? (
                <span className="text-xs text-muted-foreground">5 of {learning_count} shown</span>
              ) : undefined
            }
          />
          {learning.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              Nothing on the stand. Add a tune to start learning.
            </p>
          ) : (
            <div className="space-y-4">
              {learning.map((tune, i) => (
                <TuneCard
                  key={tune.id}
                  href={`/tunes/${tune.id}`}
                  name={tune.name}
                  metaLine={meta(tune)}
                  accent="learning"
                  delay={i * 70}
                >
                  <RecordingPlayer
                    recording={tune.primary_recording}
                    onPlay={() => setNowPlaying(tune.name)}
                  />
                  <Button
                    size="sm"
                    className="bg-learning text-primary-foreground hover:bg-learning/90"
                    onClick={() => patch(`/tunes/${tune.id}/mark_known`)}
                  >
                    Got it — I know this
                  </Button>
                </TuneCard>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionHeading kicker="Gone rusty" title="Refresh" accent="refresh" />
          {refresh.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              Nothing rusty — your repertoire's in good nick.
            </p>
          ) : (
            <div className="space-y-4">
              {refresh.map((tune, i) => (
                <TuneCard
                  key={tune.id}
                  href={`/tunes/${tune.id}`}
                  name={tune.name}
                  metaLine={meta(tune)}
                  accent="refresh"
                  delay={i * 70}
                >
                  <RecordingPlayer
                    recording={tune.primary_recording}
                    onPlay={() => setNowPlaying(tune.name)}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="bg-refresh text-primary-foreground hover:bg-refresh/90"
                      onClick={() => patch(`/tunes/${tune.id}/log_play`)}
                    >
                      Played it
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => patch(`/tunes/${tune.id}/mark_solid`)}
                    >
                      Still solid
                    </Button>
                  </div>
                </TuneCard>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionHeading
            kicker="For the love of it"
            title="Play"
            accent="play"
            aside={
              <Button
                size="sm"
                variant="outline"
                onClick={shuffle}
                disabled={play_pool.length === 0}
              >
                {shuffled ? 'Another' : 'Draw a tune'}
              </Button>
            }
          />
          {sets.length > 0 && (
            <div className="mb-4 space-y-4">
              {sets.map((set, i) => (
                <TuneCard
                  key={set.id}
                  href={`/sets/${set.id}`}
                  name={set.name}
                  metaLine={set.tune_names.join(' · ')}
                  accent="play"
                  delay={i * 70}
                >
                  <RecordingPlayer
                    recording={set.primary_recording}
                    onPlay={() => setNowPlaying(set.name)}
                  />
                  <Button
                    size="sm"
                    className="bg-play text-primary-foreground hover:bg-play/90"
                    onClick={() => patch(`/sets/${set.id}/played`)}
                  >
                    Played this set
                  </Button>
                </TuneCard>
              ))}
            </div>
          )}
          {play_pool.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">No known tunes in the book yet.</p>
          ) : shuffled ? (
            <TuneCard
              key={shuffled.id}
              href={`/tunes/${shuffled.id}`}
              name={shuffled.name}
              metaLine=""
              accent="play"
              delay={0}
            >
              <RecordingPlayer
                recording={shuffled.primary_recording}
                onPlay={() => setNowPlaying(shuffled.name)}
              />
            </TuneCard>
          ) : (
            <p className="text-sm text-muted-foreground">
              {play_pool.length} known {play_pool.length === 1 ? 'tune' : 'tunes'} in the book —
              draw one at random.
            </p>
          )}
        </section>
      </div>
    </>
  )
}
